import { useTranslation } from 'react-i18next'
import { cx } from '@/shared/lib'
import { Icon } from '@/shared/ui/Icon'
import { Tooltip } from '@/shared/ui/Tooltip'
import {
  marksOf,
  type ProductMark,
  type ProductMarkId,
  type ProductMarkSource,
  type ProductMarkTone,
} from '../model/marks'
import s from './ProductMarks.module.css'

const TONE_CLASS: Record<ProductMarkTone, string | undefined> = {
  accent: s.accent,
  danger: s.danger,
  success: s.success,
  warning: s.warning,
  neutral: s.neutral,
}

const MARK_LABEL = {
  promo: 'mark.promo',
  sale: 'mark.sale',
  new: 'mark.new',
  confirmation: 'mark.confirmation',
  incoming: 'mark.incoming',
} as const satisfies Record<ProductMarkId, string>

export type ProductMarksProps = {
  product: ProductMarkSource
  /**
   * `icons` — значки в ячейке реестра, текст — в подсказке и доступном имени; `list` — значок
   * с подписью и условиями строкой, для карточки товара.
   */
  variant?: 'icons' | 'list'
  /** Доступное имя списка `list`: «Признаки товара». */
  label?: string
  className?: string
}

/**
 * Признаки товара: акция, распродажа, новинка, «заказ требует подтверждения», «ожидается
 * поставка». У обычного товара — ничего, пустое место без заглушки. Фото-признаков нет.
 */
export function ProductMarks({ product, variant = 'icons', label, className }: ProductMarksProps) {
  const { t } = useTranslation('catalog')
  const marks = marksOf(product)
  if (marks.length === 0) return null

  const labelOf = (mark: ProductMark) =>
    mark.id === 'incoming' && mark.note !== null
      ? t('mark.incomingNote', { note: mark.note })
      : t(MARK_LABEL[mark.id])
  /* Условия акции — второй строкой: в подсказке, в доступном имени и в списке карточки */
  const noteOf = (mark: ProductMark) => (mark.id === 'incoming' ? null : mark.note)

  if (variant === 'list') {
    return (
      <ul className={cx(s.list, className)} aria-label={label}>
        {marks.map((mark) => {
          const note = noteOf(mark)
          return (
            <li key={mark.id} className={s.item}>
              <span className={cx(s.badge, TONE_CLASS[mark.tone])}>
                <Icon name={mark.icon} size="lg" />
              </span>
              <span className={s.text}>
                <span className={s.label}>{labelOf(mark)}</span>
                {note !== null ? <span className={s.note}>{note}</span> : null}
              </span>
            </li>
          )
        })}
      </ul>
    )
  }

  return (
    <span className={cx(s.icons, className)}>
      {marks.map((mark) => {
        const label = labelOf(mark)
        const note = noteOf(mark)
        const name = note === null ? label : `${label}. ${note}`
        return (
          <Tooltip
            key={mark.id}
            size={note === null ? 'xs' : 'sm'}
            {...(note === null ? { content: label } : { title: label, content: note })}
            placement="right"
            decorative
          >
            <span role="img" aria-label={name} className={cx(s.badge, TONE_CLASS[mark.tone])}>
              <Icon name={mark.icon} size="lg" />
            </span>
          </Tooltip>
        )
      })}
    </span>
  )
}
